import React, { useEffect, useState } from 'react'
import { useUser } from 'util/UserContext'
import api from 'config/axiosConfig'
import CreateSolution from './CreateSolution'
import SolutionCard from './SolutionCard'
import AddButton from 'ui/AddButton'

import { Issue, Solution } from 'util/constants'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import { AxiosError } from 'axios'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'
import ScrollHideContainer from 'ui/ScrollHideContainer'

interface Props {
  issue: Issue
}

interface SolutionResponse {
  id: number
  issue_id: number
  solution_id: number
  created_at: string
  updated_at: string
  user_id: number
  upvotes: number
  downvotes: number
}

const SolutionsSection = ({ issue }: Props) => {
  const { currentUser, setShowAuthModal } = useUser()
  const [solutions, setSolutions] = useState<Solution[]>([])
  const [error, setError] = useState('')
  const [openModal, setOpenModal] = useState(false)

  useEffect(() => {
    const fetchSolutions = async () => {
      try {
        const response = await api.get(`/api/solutions/issue/${issue.id}`)
        const solutionIds = response.data.map(
          (solution: SolutionResponse) => solution.solution_id
        )

        if (solutionIds.length === 0) {
          setSolutions([])
          return
        }

        const solutionsResponse = await api.get(
          `/api/solutions?solutionIds=${JSON.stringify(solutionIds)}`
        )
        setSolutions(solutionsResponse.data)
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 404) {
            setError('404: Failed to load solutions. Please try again later.')
        } else {
            setError('Failed to load solutions. Please try again later.')
        }
      }
    }

    if (issue) {
      fetchSolutions()
    }
  }, [issue])

  const handleSolutionAdded = async () => {
    try {
      const response = await api.get(`/api/solutions/issue/${issue.id}`)
      const solutionIds = response.data.map(
        (solution: SolutionResponse) => solution.solution_id
      )

      if (solutionIds.length === 0) {
        setSolutions([])
        return
      }

      const solutionsResponse = await api.get(
        `/api/solutions?solutionIds=${JSON.stringify(solutionIds)}`
      )
      setSolutions(solutionsResponse.data)
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 404) {
        setError('404: Failed to load solutions. Please try again later.')
      } else {
        setError('Failed to load solutions. Please try again later.')
      }
    }
  }

  const handleOpenModal = () => {
    if (!currentUser) {
      setShowAuthModal(true)
    } else {
      setOpenModal(true)
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  if (error) {
    return error.match(/404/) ? <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find any solutions for this issue.`} buttonText={`View Other Solutions`} buttonHref={`/solutions/new`} /> : <ServerErrorPage />
  }

  if (!issue) {
    return (
      <LoadingSkeleton count={5} />
    )
  }

  if (!solutions) {
    return (
      <LoadingSkeleton count={5} />
    )
  }

  return (
    <div className="solutions-section pb-6">
      <ScrollHideContainer 
        className="z-40 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg border-b border-eoe-border-main dark:border-eoe-dark-border-main"
        hideOffset={115}
        threshold={114}
      >
      <div className="flex flex-col space-y-1 mb-1">
        <div className="flex mb-1 mx-1">
          <AddButton
            tooltipTitle="Add a solution"
            buttonText="Solution"
            onClick={handleOpenModal}
            className="mx-2 w-full"
          />
        </div>
      </div>
      </ScrollHideContainer>
      {openModal && (
        <CreateSolution
          issueId={issue.id}
          onSolutionAdded={handleSolutionAdded}
          onClose={handleCloseModal}
          open={openModal}
        />
      )}
      <div className="entity-divider">
        {solutions.length === 0 ? (
          <p className="m-4 text-sm">Be the first to suggest a solution for this issue!</p>
        ) : (
          solutions.map((solution) => (
            <SolutionCard
              key={`${solution.id}`}
              solution={solution}
              truncateDescription={true}
              isSolutionProfilePage={false}
              isEntitySection={true}
            />
          ))
        )}
      </div>
    </div>
  )
}

export default SolutionsSection
